import { useState } from "react";
import { X, Search, RefreshCw, CheckCircle, Plus, Star, Bot } from "lucide-react";
import getApiUrl from '../../utils/getApiUrl';

const API = getApiUrl();

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


const RechercheIAModal = ({ typesPrescripteurs, onImport, onClose }) => {
  const [departement, setDepartement] = useState("");
  const [typesSelectionnes, setTypesSelectionnes] = useState([]);
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState([]);

  const search = async () => {
    if (!departement) return alert("Veuillez entrer un département");
    setSearching(true);
    try {
      const resp = await authFetch(`${API}/api/grands-comptes/ia/recherche-prescripteurs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          departement,
          types: typesSelectionnes.length > 0 ? typesSelectionnes : null
        })
      });
      if (resp.ok) {
        const data = await resp.json();
        setResults(data.prescripteurs_trouves || []);
      }
    } catch (e) {
      console.error(e);
    }
    setSearching(false);
  };

  const importSelected = async () => {
    const toImport = results.filter((_, i) => selected.includes(i));
    for (const p of toImport) {
      try {
        await authFetch(`${API}/api/grands-comptes/prescripteurs`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(p)
        });
      } catch (e) {
        console.error(e);
      }
    }
    onImport();
    onClose();
  };

  const toggleType = (type) => {
    setTypesSelectionnes(prev => 
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm overflow-y-auto p-4">
      <div className="bg-slate-800 border border-slate-600 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-slate-800 border-b border-slate-700 p-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Bot className="w-6 h-6 text-purple-400" /> Recherche IA de prescripteurs
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-700 rounded-lg">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>
        
        <div className="p-6 space-y-6">
          <div className="bg-slate-700/30 rounded-xl p-4 space-y-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Département *</label>
              <input
                type="text"
                value={departement}
                onChange={e => setDepartement(e.target.value)}
                placeholder="Ex: 13, 75, 69..."
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm text-slate-400 mb-2">Types (optionnel)</label>
              <div className="flex flex-wrap gap-2">
                {Object.entries(typesPrescripteurs).map(([key, info]) => (
                  <button
                    key={key}
                    onClick={() => toggleType(key)}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      typesSelectionnes.includes(key)
                        ? "bg-purple-600 text-white"
                        : "bg-slate-700 text-slate-300 hover:bg-slate-600"
                    }`}
                  >
                    {info.nom_complet}
                  </button>
                ))}
              </div>
            </div>
            
            <button
              onClick={search}
              disabled={searching}
              className="w-full py-3 bg-purple-600 hover:bg-purple-500 text-white rounded-lg flex items-center justify-center gap-2"
            >
              {searching ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
              {searching ? "Recherche..." : "Lancer la recherche IA"}
            </button>
          </div>
          
          {results.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">{results.length} prescripteur(s)</h3>
                <button
                  onClick={() => setSelected(results.map((_, i) => i))}
                  className="text-sm text-blue-400 hover:text-blue-300"
                >
                  Tout sélectionner
                </button>
              </div>
              
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {results.map((p, idx) => (
                  <div
                    key={idx}
                    onClick={() => setSelected(prev => 
                      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
                    )}
                    className={`bg-slate-700/30 rounded-lg p-4 cursor-pointer border-2 transition-colors ${
                      selected.includes(idx) ? "border-purple-500" : "border-transparent hover:border-slate-600"
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-white font-medium">{p.nom_organisme}</p>
                        <p className="text-sm text-slate-400">{typesPrescripteurs[p.type_prescripteur]?.nom_complet}</p>
                        <p className="text-sm text-slate-500">{p.code_postal} {p.ville}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {p.score_potentiel && (
                          <span className="text-yellow-400 flex items-center gap-1">
                            <Star className="w-4 h-4" /> {p.score_potentiel}/10
                          </span>
                        )}
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          selected.includes(idx) ? "bg-purple-600 border-purple-600" : "border-slate-500"
                        }`}>
                          {selected.includes(idx) && <CheckCircle className="w-4 h-4 text-white" />}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <button
                onClick={importSelected}
                disabled={selected.length === 0}
                className="w-full py-3 bg-green-600 hover:bg-green-500 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-lg flex items-center justify-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Importer {selected.length} prescripteur(s)
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RechercheIAModal;
